import { postPreparedRequests } from '@ipfabric/api-client/client/model/prepared-requests/prepared-requests';
import { isFeatureFlagEnabled } from '@ipfabric/ui-components/utils/featureFlags';
import type {
  TableRequestColumns,
  TableWithReportsRequestBody,
} from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { selectTableId } from 'components/ApiTable/model/selectors/selectTableId';
import { selectTableRequestPayloadBase } from 'components/ApiTable/model/selectors/selectTableRequestPayloadBase';
import { isBackendColumn } from 'components/Table/model/isBackendColumn';
import { selectVisibleColumns } from 'components/Table/model/selectors/selectVisibleColumns';
import { array } from 'fp-ts';
import { pipe } from 'fp-ts/lib/function';
import type { TableEndpoint } from 'packages/utils/src/lib/table/TableEndpoint';
import { apiEndpointToFullUrl } from 'utils/apiEndpointToFullUrl';
import type { ApiTableCommandFactory } from '../ApiTableModel';

const prepareDownloadUrl = async (
  tableEndpoint: TableEndpoint<any>,
  requestData: TableWithReportsRequestBody<any>,
) => {
  const preparedRequest = await postPreparedRequests(requestData, {
    method: 'POST',
    path: tableEndpoint.apiEndpoint,
  });

  return apiEndpointToFullUrl(
    `/prepared-requests/${preparedRequest.preparedRequestId}/execute`,
  );
};

type DownloadCsvCommand = {
  override?: Partial<TableWithReportsRequestBody<any>>;
};

export const downloadCsv: ApiTableCommandFactory<
  DownloadCsvCommand | undefined
> = (cmdContext) => async (command) => {
  const columns = pipe(
    cmdContext.getTableModel(),
    selectVisibleColumns,
    array.filter(isBackendColumn),
    array.map(prop('key')),
  ) as TableRequestColumns<any>;

  const tableEndpoint = cmdContext.getState().endpoint;
  const requestData: TableWithReportsRequestBody<any> = {
    ...selectTableRequestPayloadBase(cmdContext.getState()),
    reports: selectTableId(cmdContext.getState()),
    columns,
    format: {
      dataType: 'csv',
      exportToFile: `${tableEndpoint.title.replace(/[^a-z0-9]+/gi, '_') || 'table'}.csv`,
      options: {
        timezone: cmdContext.getState().timezone,
        asnFormat: isFeatureFlagEnabled('ENABLE_BGP_ASPLAIN_FORMAT')
          ? 'asplain'
          : 'asdot',
      },
    },
    ...command?.override,
  };

  const downloadUrl = await prepareDownloadUrl(tableEndpoint, requestData);

  if (downloadUrl) {
    window.location.href = downloadUrl;
  }
};
